import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getSessions, deleteSession, updateSession } from '../api/chat'
import type { ChatSessionItem } from '../api/chat'
import { getSidebarAssistants, pinAgent, unpinAgent } from '../api/assistants'
import type { SidebarAssistant } from '../api/assistants'
import { mergeSidebarRows } from './sidebarRows'
import type React from 'react'

type Args = {
	userExists: boolean
	refreshKey?: number
	currentPathname: string
	navigateToChatRoot: () => void
}

/** Страница списка диалогов (владелец 17.10: «по скроллу и всегда отдавать по 30, они все равно все в экран не
 *  влазят»). Раньше панель брала первые 30 и всё: тридцать первый диалог хранился, но человек до него не добирался. */
export const SIDEBAR_PAGE = 30
/** Потолок одного запроса на сервере (chat_sessions.list_sessions: limit ≤ 100) — столько перезагрузка просит разом. */
const SERVER_PAGE_MAX = 100

/** Первая страница: чаты сайта + умные помощники с работами (23.10); сбой второго списка не валит первый. */
async function fetchFirst(limit: number): Promise<{ chats: ChatSessionItem[]; extra: SidebarAssistant[] }> {
	const [chats, extra] = await Promise.all([getSessions(limit), getSidebarAssistants().then((r) => r.assistants ?? []).catch(() => [])])
	return { chats, extra }
}

export const useSidebarSessions = ({ userExists, refreshKey, currentPathname, navigateToChatRoot }: Args) => {
	// Чаты сайта — страницами с сервера (закреплённые первыми, дальше по активности), помощники с работами — отдельным
	// списком; на экран идёт их слияние (sidebarRows): закреплённые наверху, дальше по активности.
	const [chats, setChats] = useState<ChatSessionItem[]>([])
	const [extra, setExtra] = useState<SidebarAssistant[]>([])
	const [hasMore, setHasMore] = useState(false)
	const [reloadTick, setReloadTick] = useState(0)
	const sessions = useMemo(() => mergeSidebarRows(chats, extra), [chats, extra])
	const sessionsRef = useRef<ChatSessionItem[]>([])
	useEffect(() => { sessionsRef.current = sessions }, [sessions])
	// Сколько строк чатов сервер уже выдал по порядку — с этого места просим следующую страницу. Удаление чата сдвигает
	// серверный список на строку назад, поэтому счётчик уменьшается вместе с ним; новый чат сдвигает вперёд — повтор
	// последней строки на следующей странице отсекается по id.
	const offsetRef = useRef(0)
	const hasMoreRef = useRef(false)
	const loadingRef = useRef(false)
	useEffect(() => { hasMoreRef.current = hasMore }, [hasMore])

	useEffect(() => {
		if (!userExists) return
		let alive = true
		// Перезагрузка (новый чат, смена аккаунта) держит уже долистанную длину, а не сбрасывает панель к 30.
		const want = Math.min(SERVER_PAGE_MAX, Math.max(SIDEBAR_PAGE, offsetRef.current))
		fetchFirst(want)
			.then((rows) => {
				if (!alive) return
				setChats(rows.chats)
				setExtra(rows.extra)
				offsetRef.current = rows.chats.length
				setHasMore(rows.chats.length >= want)
			})
			.catch(() => undefined)
		return () => { alive = false }
	}, [userExists, refreshKey, reloadTick])

	const loadSessions = useCallback(async () => { setReloadTick((t) => t + 1) }, [])

	/** Следующие 30 — когда метка под списком попала в экран (SidebarChats). Повторный вызов во время запроса и после
	 *  короткой страницы ничего не делает. */
	const loadMore = useCallback(async () => {
		if (!userExists || !hasMoreRef.current || loadingRef.current) return
		loadingRef.current = true
		try {
			const page = await getSessions(SIDEBAR_PAGE, offsetRef.current)
			offsetRef.current += page.length
			setHasMore(page.length === SIDEBAR_PAGE)
			if (page.length) {
				setChats((prev) => {
					const known = new Set(prev.map((s) => s.id))
					const fresh = page.filter((s) => !known.has(s.id))
					return fresh.length ? [...prev, ...fresh] : prev
				})
			}
		} catch {
			/* сеть: следующее пересечение метки попробует снова */
		} finally {
			loadingRef.current = false
		}
	}, [userExists])

	const deleteOne = useCallback(
			async (e: React.MouseEvent, sessionId: string) => {
				e.stopPropagation()
				try {
					// Помощника из меню не удаляют: он уходит сам, когда работ не осталось (кнопки у строки нет).
					if (sessionsRef.current.find((s) => s.id === sessionId)?.kind === 'assistant') return
					await deleteSession(sessionId)
					setChats((prev) => prev.filter((s) => s.id !== sessionId))
					offsetRef.current = Math.max(0, offsetRef.current - 1)
					if (currentPathname === `/chat/${sessionId}`) navigateToChatRoot()
				} catch {
					/* ignore */
				}
			},
			[currentPathname, navigateToChatRoot]
	)

	const renameOne = useCallback(async (sessionId: string, title: string) => {
		try {
			// Строка помощника носит его имя — переименовывать нечего (кнопки у неё нет).
			if (sessionsRef.current.find((s) => s.id === sessionId)?.kind === 'assistant') return
			await updateSession(sessionId, { title })
			setChats((prev) => prev.map((s) => (s.id === sessionId ? { ...s, title } : s)))
		} catch {
			/* ignore */
		}
	}, [])

	/** Закрепить / открепить строку (23.10): чат — полем сессии, помощник — той же скрепкой, что в рельсе раздела
	 *  (предел скрепок держит сервер; отказ — словами). Раздел помощников узнаёт о скрепке событием. */
	const pinOne = useCallback(async (e: React.MouseEvent, sessionId: string) => {
		e.stopPropagation()
		const row = sessionsRef.current.find((s) => s.id === sessionId)
		if (!row) return
		try {
			if (row.kind === 'assistant' && row.agent_id) {
				const r = row.is_pinned ? await unpinAgent(row.agent_id) : await pinAgent(row.agent_id)
				const ids = r.pinned_agent_ids ?? []
				setExtra((prev) => prev.map((a) => ({ ...a, pinned: ids.includes(a.agent_id) })))
				window.dispatchEvent(new CustomEvent('umnik:assistant-pins', { detail: ids }))
				return
			}
			const next = !row.is_pinned
			await updateSession(sessionId, { is_pinned: next })
			setChats((prev) => prev.map((s) => (s.id === sessionId ? { ...s, is_pinned: next } : s)))
		} catch (err) {
			const msg = err instanceof Error ? err.message : ''
			if (msg) window.dispatchEvent(new CustomEvent('umnik:toast', { detail: { text: msg, kind: 'error' } }))
		}
	}, [])

	return { sessions, loadSessions, deleteOne, renameOne, pinOne, hasMore, loadMore }
}
